import { useEffect, useState, type ReactNode } from 'react'
import { useCountUp } from '../lib/motion'
import { useNav } from '../lib/nav'
import { Page } from '../components/Shell'
import { I } from '../components/Icons'
import { useStore } from '../state/store'
import { loadToday, loadPendingIntakes, loadSales, loadLast7, browsePieces, loadOnClock, countWebNew, type Punch } from '../lib/api'
import { prefetch, useCached } from '../lib/cache'
import { nyToday, readDay } from '../state/day'
import type { PendingIntake } from '../lib/types'
import { loadQueue, queueKey, prefetchQueue, type QueueResult } from '../lib/qa'
import { fmtC } from '../lib/types'
import { usePhone } from '../lib/device'
import { PhoneHomeBar } from '../components/Phone'

export function Home() {
  const nav = useNav()
  const { intakeNo, employees } = useStore()
  // The website Inbox is for owners only: its tile shows only when the person signed in to the day is an owner
  const meId = readDay()?.employee_id ?? ''
  const isOwner = employees.some((e) => e.employee_id === meId && e.rights === 'owner' && e.active)
  const W = useCached<number>('webNew', countWebNew, { staleMs: 30_000, enabled: isOwner })
  const webNew = W.data ?? null
  // Live numbers on the tiles (F5): fetched after the tiles paint; a failed read shows the plain subtitle.
  // D1/D2: today's numbers and the pending count come from the read cache (the Sales and Pending screens share them), and
  // Home warms what Sell and Sales open with, so those screens have their lists before anyone taps
  const T = useCached<{ total_cents: number; sales: number }[]>(`today:${nyToday()}`, loadToday, { staleMs: 0 })
  const P = useCached<PendingIntake[]>('pending', loadPendingIntakes, { staleMs: 0 })
  const today = T.data ? { total: T.data.reduce((a, x) => a + Number(x.total_cents), 0), n: T.data.reduce((a, x) => a + Number(x.sales), 0) } : null
  // Motion: today's total counts up when it lands (and when it changes); the other numbers fade in (the `--live` class on a
  // subtitle that has data). The tiles come in one after another only right after sign-in, on the welcome event from App.
  const todayShown = useCountUp(today ? today.total : 0, 500)
  const [cascade, setCascade] = useState(false)
  useEffect(() => {
    const on = () => setCascade(true)
    window.addEventListener('starchive:welcomed', on); return () => window.removeEventListener('starchive:welcomed', on)
  }, [])
  const pending = P.data ? P.data.length : null
  const QQ = useCached<QueueResult>(queueKey(), () => loadQueue(), { staleMs: 60_000 })
  const qa = QQ.data ? { n: QQ.data.rows.length, more: !!QQ.data.cursor } : null
  const C = useCached<Punch[]>(`onclock:${nyToday()}`, loadOnClock, { staleMs: 0 })
  const onClock = C.data ? C.data.length : null
  const warm = (to: string) => {
    if (to === '/sell') prefetch('rack', () => browsePieces(null), 60_000)
    if (to === '/sales') { prefetch('sales', () => loadSales(), 10_000); prefetch('last7', loadLast7, 60_000) }
    if (to === '/qa') prefetchQueue()
  }
  useEffect(() => { warm('/sell'); warm('/sales') }, [])
  const phone = usePhone()
  const hour = new Date().getHours()
  const greet = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening'
  const tiles: { t: string; s: string; i: ReactNode; to: string; light?: boolean; phase?: string; live?: boolean }[] = [
    { t: 'Sell', s: 'Cash, Zelle or crypto', i: I.sell(26), to: '/sell' },
    { t: 'Intake', s: pending ? `${pending} pending from trades` : 'Buy or consign a piece', i: I.intake(26), to: '/intake', live: !!pending },
    { t: 'Trade', s: 'Take pieces, give pieces', i: I.trade(26), to: '/trade' },
    { t: 'Listing QA', s: qa === null ? 'Scan a tag, approve or send back' : qa.n === 0 ? (phone ? 'Nothing waiting' : 'Nothing waiting for a check') : `${qa.n}${qa.more ? '+' : ''} waiting${phone ? '' : ' for a check'}`, i: I.qa(26), to: '/qa', light: true, live: qa !== null },
    { t: "Today's sales", s: today ? `${fmtC(Math.round(todayShown))} · ${today.n} ${today.n === 1 ? 'sale' : 'sales'}${phone ? '' : ', all channels'}` : 'Every sale signed today', i: I.sales(26), to: '/sales', light: true, live: !!today },
    { t: 'Time clock', s: onClock === null ? 'Clock in, clock out' : onClock === 0 ? 'Nobody on the clock' : `${onClock} on the clock`, i: I.clock(26), to: '/time', light: true, live: onClock !== null },
    { t: 'Close out', s: phone ? 'Checklist' : 'Checklist and clock out', i: I.close(26), to: '/close', light: true },
  ]
  // Home v3 (iPad 3B): the faded STARCHIVE wordmark sits level with the rail star, the greeting uses the first name, and
  // today's numbers are one quiet line under it instead of a bar at the top
  const first = (readDay()?.name || '').trim().split(/\s+/)[0]
  const stats: { v: string; l: string }[] = []
  if (today) stats.push({ v: fmtC(Math.round(todayShown)), l: 'today' })
  if (qa && !phone) stats.push({ v: `${qa.n}${qa.more ? '+' : ''}`, l: 'in Listing QA' })
  if (onClock !== null && !phone) stats.push({ v: String(onClock), l: 'on the clock' })
  if (intakeNo) stats.push({ v: `#${String(intakeNo).padStart(4, '0')}`, l: 'next intake' })
  return (
    <Page bar={phone ? <PhoneHomeBar /> : <header className="home__brand"><span className="wm" role="img" aria-label="Starchive">STARCHIVE</span></header>}>
      <div className="home home--v3">
        <div className={'home__hello' + (cascade ? ' home__hello--in' : '')}>{greet}{first ? `, ${first}` : ''}.</div>
        <div className="home__stats">
          {stats.map((x, k) => (
            <span key={x.l} className="home__stat">{k > 0 && <span className="home__dot" aria-hidden="true">·</span>}<b className="m-fadein">{x.v}</b> {x.l}</span>
          ))}
        </div>
        <div className="tiles">
          {tiles.filter((x) => !x.light).map((x) => (
            <button key={x.t} type="button" className={'tile' + (x.light ? ' tile--light' : '') + (cascade ? ' tile--in' : '')} style={cascade ? { ['--i' as string]: tiles.indexOf(x) } : undefined} disabled={!!x.phase} onPointerDown={() => warm(x.to)} onClick={() => nav(x.to)}>
              <span className="tile__icon">{x.i}</span>
              <span><span className="tile__title">{x.t}</span><span key={x.live ? 'live' : 'plain'} className={'tile__sub' + (x.live ? ' m-fadein' : '')}>{x.s}</span></span>
              {x.phase && <span className="tile__badge">{x.phase}</span>}
            </button>
          ))}
        </div>
        <div className="tiles tiles--light">
          {tiles.filter((x) => x.light).map((x) => (
            <button key={x.t} type="button" className={'tile' + (x.light ? ' tile--light' : '') + (cascade ? ' tile--in' : '')} style={cascade ? { ['--i' as string]: tiles.indexOf(x) } : undefined} disabled={!!x.phase} onPointerDown={() => warm(x.to)} onClick={() => nav(x.to)}>
              <span className="tile__icon">{x.i}</span>
              <span><span className="tile__title">{x.t}</span><span key={x.live ? 'live' : 'plain'} className={'tile__sub' + (x.live ? ' m-fadein' : '')}>{x.s}</span></span>
              {x.phase && <span className="tile__badge">{x.phase}</span>}
            </button>
          ))}
        </div>
        {isOwner && (
          <>
            <div className="home__label">Owners</div>
            <div className="otiles">
              <button type="button" className={'otile' + (cascade ? ' tile--in' : '')} onClick={() => nav('/inbox')}>
                <span className="otile__icon">{I.inbox(22)}</span>
                <span className="otile__text"><span className="otile__title">Inbox</span><span key={webNew !== null ? 'live' : 'plain'} className={'otile__sub' + (webNew !== null ? ' m-fadein' : '')}>{webNew ? `${webNew} waiting from the website` : 'Sell or consign and contact forms'}</span></span>
                {!!webNew && <span className="otile__count" aria-label={`${webNew} new`}>{webNew}</span>}
              </button>
              <button type="button" className={'otile' + (cascade ? ' tile--in' : '')} onClick={() => nav('/team')}>
                <span className="otile__icon">{I.team(22)}</span>
                <span className="otile__text"><span className="otile__title">Team</span><span className="otile__sub">Employee IDs and rights</span></span>
                <span className="otile__chev">{I.chev()}</span>
              </button>
            </div>
          </>
        )}
      </div>
    </Page>
  )
}
